"use client";

import React, { useState, useEffect } from "react";
import { Badge } from "./Badge";
import { LanguageSelectDropdown } from "../wizard/LanguageSelectDropdown";
import { Icon } from "./Icon";
import { RequiredMark } from "./RequiredMark";
import { CdnIcon } from "@westayltd/icons/react";
import { useActiveLanguages, type ActiveLanguage } from "@/hooks/useActiveLanguages";
import { useContentLocaleStore } from "@/stores/locale-store";
import { useIsWizardReadOnly } from "@/hooks/useIsWizardReadOnly";
import { useDraftOnOpen } from "@/hooks/useDraftOnOpen";

export type Language = ActiveLanguage;

/** The one "add/manage translations" badge look, shared by every field across
 * every step — do not fork a second style; extend this if the design changes.
 * Exported so `TermToggleRow` (the Rules step's per-term trigger, which opens
 * a different modal entirely) can render the identical badge. */
export const VARIANT_BADGE_CLASSNAME =
  "gap-1 px-2 py-0.5 text-[10px] font-medium cursor-pointer transition-colors rounded-md bg-red-50 text-red-600 border border-red-100 hover:bg-red-100";
export const ADD_TRIGGER_CLASSNAME =
  "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-secondary-50 text-secondary-700 border border-secondary-200 hover:bg-secondary-100 transition-colors cursor-pointer shrink-0 whitespace-nowrap";

/** Shared by both the single-line and multiline variant editors in the modal below — keep in sync. */
const VARIANT_INPUT_CLASSNAME =
  "w-full min-w-0 px-4 py-3 bg-bg rounded-xl text-sm text-foreground-950 placeholder:text-foreground-400 border border-foreground-100 focus:border-primary focus:outline-none focus:ring-primary-100 transition-all";
const VARIANT_INPUT_PLACEHOLDER = "e.g. translation...";

/** "+N variant"/"+N variants" — shared by every variant-count badge (TranslatableField, TermToggleRow, CategoryEditorModal). */
export function variantCountLabel(count: number): string {
  return `+${count} variant${count > 1 ? "s" : ""}`;
}

/**
 * The one "add/manage translations" trigger row — badge (opens the modal) and
 * "add a language" dropdown (adds one straight from outside it), shown
 * together whenever both apply. Shared by every caller with a flat
 * language-list model (TranslatableField, CategoryEditorModal) so the control
 * looks and behaves identically everywhere instead of each one re-building it.
 */
export function VariantTrigger({
  count,
  remainingLanguages,
  onOpenModal,
  triggerLabel,
}: {
  count: number;
  remainingLanguages: Language[];
  onOpenModal: () => void;
  triggerLabel?: string;
}) {
  return (
    <>
      {count > 0 && (
        <Badge variant="error" className={VARIANT_BADGE_CLASSNAME} onClick={onOpenModal}>
          <CdnIcon name="interface-text-formatting-translate--options-text-translate--Streamline-Core" size={9} />
          <span className="flex">{variantCountLabel(count)}</span>
        </Badge>
      )}

      {remainingLanguages.length > 0 && (
        <button type="button" onClick={onOpenModal} className={ADD_TRIGGER_CLASSNAME}>
          <CdnIcon name="interface-text-formatting-translate--options-text-translate--Streamline-Core" size={9} />
          {triggerLabel ?? "Add translation"}
        </button>
      )}
    </>
  );
}

/**
 * Wraps an English form control with translation triggers and an editor modal
 * for the field's active-language variants.
 */
export function TranslatableField({
  label,
  required,
  hasValue = true,
  variants = [],
  onVariantsChange,
  children,
  triggerLabel,
  referenceValue,
  maxLength,
  multiline = false,
  triggerAbove = false,
}: {
  label: string;
  required?: boolean;
  hasValue?: boolean;
  variants?: { lang: Language; value: string }[];
  onVariantsChange?: (variants: { lang: Language; value: string }[]) => void;
  children: React.ReactNode;
  /** Overrides the default "Add translation" trigger text. */
  triggerLabel?: string;
  /** The current English value, shown in the modal's reference row — omit only
   * when there genuinely isn't one yet. */
  referenceValue?: string;
  /** Same cap as the English field, if it has one — enforced on every variant
   * input too, since a translation is validated by the same API field length
   * limit the English value is. Omit when the English field has no limit. */
  maxLength?: number;
  /** Set when `children` is a `<Textarea>` — renders each variant's own input
   * as a textarea too, since cramming a long translated paragraph into a
   * single-line input (the previous behavior here) is unreadable to edit. */
  multiline?: boolean;
  /** With an empty `label`, the trigger badge/dropdown normally renders below
   * `children` (e.g. Bedrooms/Bathrooms-style rows). Set this when the field
   * has no visible caption of its own but still needs the trigger sitting
   * above the input instead — otherwise it reads as floating, detached UI
   * below an unrelated-looking row (e.g. a bare list-item input). */
  triggerAbove?: boolean;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localVariants, setLocalVariants] = useState<{ lang: Language; value: string }[]>([]);
  const { languages: availableLanguages } = useActiveLanguages();
  // A read-only property (e.g. an approved version opened by someone who
  // can't edit it) has nothing this trigger can do — the add/manage-
  // translations action always ends in a save nobody can make.
  const isReadOnly = useIsWizardReadOnly();
  const preferredTranslationLocale = useContentLocaleStore(
    (s) => s.preferredTranslationLocale,
  );

  const activeVariants = onVariantsChange ? variants : localVariants;
  const updateVariants = (newVariants: typeof activeVariants | ((prev: typeof activeVariants) => typeof activeVariants)) => {
    const computed = typeof newVariants === 'function' ? newVariants(activeVariants) : newVariants;
    if (onVariantsChange) {
      onVariantsChange(computed);
    } else {
      setLocalVariants(computed);
    }
  };

  // Local draft — edits inside the open modal only reach `activeVariants`
  // (and its badge) once Done is clicked, and only for rows with real
  // content. The preferred-locale auto-add effect below bypasses this and
  // writes straight to activeVariants, since it must work with the modal closed.
  const [draftVariants, setDraftVariants] = useDraftOnOpen(isModalOpen, activeVariants);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  // A brand-new variant starts as a copy of the English value, not blank —
  // overwriting a close starting point is easier than typing from scratch,
  // and the user can still change or clear it freely. Added to the draft
  // only — Done decides what actually reaches activeVariants.
  const handleLanguageSelect = (lang: Language) => {
    setDraftVariants(prev => (prev.some(v => v.lang.code === lang.code) ? prev : [...prev, { lang, value: referenceValue ?? '' }]));
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (!preferredTranslationLocale || isReadOnly) return;
    const lang = availableLanguages.find(
      (item) => item.code === preferredTranslationLocale,
    );
    if (!lang) return;
    if (activeVariants.some((v) => v.lang.code === lang.code)) return;
    updateVariants((prev) => [...prev, { lang, value: referenceValue ?? "" }]);
  }, [preferredTranslationLocale, availableLanguages, isReadOnly]);

  // The outer trigger (outside the modal) reflects what's actually saved —
  // the in-modal picker reflects the draft, so it doesn't re-offer a language
  // already added there but not yet committed.
  const remainingLanguages = availableLanguages.filter(
    (lang) => !activeVariants.some((v) => v.lang.code === lang.code)
  );
  const remainingDraftLanguages = availableLanguages.filter(
    (lang) => !draftVariants.some((v) => v.lang.code === lang.code)
  );

  // A variant added (manually or via the preferred-locale auto-add below) but
  // never actually filled in isn't a real translation yet — the close/Done
  // handlers already drop these from the saved list, but the badge should
  // stop counting them the moment they're added, not just once you close.
  const filledVariantCount = activeVariants.filter((v) => v.value.trim() !== "").length;

  const trigger = isReadOnly ? null : (
    <VariantTrigger
      count={filledVariantCount}
      remainingLanguages={hasValue ? remainingLanguages : []}
      onOpenModal={() => setIsModalOpen(true)}
      triggerLabel={triggerLabel}
    />
  );

  return (
    <div className={`relative flex min-w-0 flex-col ${label === "" ? "" : "mt-6"}`}>
      {label !== "" ? (
        // min-h keeps this row a constant height whether or not the trigger
        // badge/dropdown is showing — otherwise a field sitting next to a
        // plain (non-translatable) field in the same row, or another
        // TranslatableField in a different value state, drifts out of
        // vertical alignment the moment a translation gets added.
        <div className="flex min-h-[26px] items-start gap-2 flex-wrap mb-2">
          <label className="text-sm font-medium text-foreground-700 shrink-0 leading-5">
            {label}
            {required && <RequiredMark className="ml-1" />}
          </label>
          <div className="flex items-center gap-2 flex-wrap">{trigger}</div>
        </div>
      ) : triggerAbove ? (
        <div className="flex items-center gap-2 flex-wrap mb-2">{trigger}</div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap order-last mt-1">{trigger}</div>
      )}

      {/* This is where your input will render! A flex item of the column
          above, so it needs its own min-w-0 too — otherwise an unbreakable
          long value inside (e.g. a single long word with no spaces) refuses
          to shrink and the whole field overflows its grid/flex column. */}
      <div className="min-w-0 mt-1">
        {children}
      </div>

      {/* Language Variants Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg min-w-0 flex flex-col max-h-[90vh]">

            {/* Modal Header */}
            <div className="px-6 py-5 flex items-center justify-between gap-4">
              <div className="min-w-0">
                {/* Falls back to referenceValue for triggers that pass an empty label
                    on purpose (e.g. an inline field with no separate caption row) —
                    otherwise the title renders as the broken "for ''" . line-clamp +
                    break-all caps it at 2 lines even for a long, space-free custom
                    value (e.g. a junk floor-detail label) that would otherwise blow
                    the modal header out to dozens of lines. */}
                <h2
                  className="line-clamp-2 break-all text-lg font-serif text-secondary"
                  title={label || referenceValue}
                >
                  Language variants for &apos;{label || referenceValue}&apos;
                </h2>
                <p className="text-xs text-foreground-500 mt-1">Manage translations across all languages</p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-400 hover:text-foreground-700 transition-colors"
              >
                <Icon name="close" size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto flex-1 space-y-8">
              {/* Reference Field */}
              <div className="bg-bg px-6 py-5">
                <h3 className="text-[10px] font-bold text-foreground-500 uppercase tracking-wider">English (Reference)</h3>
                <div
                  className={`line-clamp-3 break-all ${referenceValue ? "text-sm text-foreground-900" : "text-sm italic text-foreground-400"}`}
                  title={referenceValue}
                >
                  {referenceValue || "No content yet"}
                </div>
              </div>

              {/* Added Variants List */}
              <div className="space-y-6 px-4 mb-2">
                {draftVariants.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-foreground-100 rounded-xl bg-background-50/50">
                    <div className="w-12 h-12 bg-white shadow-sm border border-background-100 rounded-full flex items-center justify-center mx-auto mb-3 text-foreground-400">
                      <i className="ri-translate-2 text-xl"></i>
                    </div>
                    <p className="text-sm font-medium text-foreground-700">No language variants yet</p>
                    <p className="text-xs text-foreground-500 mb-4 mt-1">Add a variant using the button below</p>
                  </div>
                ) : (
                  draftVariants.map((variant) => (
                    <div key={variant.lang.code} className="p-4 rounded-xl space-y-2 border border-foreground-100/70 min-w-0">
                      <div className="flex items-center justify-between mb-2 gap-2">
                        <h3 className="min-w-0 truncate text-sm font-medium text-foreground-700 flex items-center gap-2">
                          <span className="text-base">{variant.lang.flag}</span> {variant.lang.name}
                        </h3>
                        <button
                          type="button"
                          onClick={() => setDraftVariants(prev => prev.filter(v => v.lang.code !== variant.lang.code))}
                          className="shrink-0 text-foreground-400 hover:text-red-500 transition-colors"
                          aria-label={`Remove ${variant.lang.name} variant`}
                          title="Remove variant"
                        >
                          <Icon name="trash" size={18} />
                        </button>
                      </div>
                      {multiline ? (
                        <textarea
                          className={`${VARIANT_INPUT_CLASSNAME} resize-none`}
                          placeholder={VARIANT_INPUT_PLACEHOLDER}
                          aria-label={`Translation for ${variant.lang.name}`}
                          rows={4}
                          value={variant.value}
                          maxLength={maxLength}
                          onChange={(e) => setDraftVariants(prev => prev.map(v => v.lang.code === variant.lang.code ? { ...v, value: e.target.value } : v))}
                        />
                      ) : (
                        <input
                          type="text"
                          className={VARIANT_INPUT_CLASSNAME}
                          placeholder={VARIANT_INPUT_PLACEHOLDER}
                          aria-label={`Translation for ${variant.lang.name}`}
                          value={variant.value}
                          maxLength={maxLength}
                          onChange={(e) => setDraftVariants(prev => prev.map(v => v.lang.code === variant.lang.code ? { ...v, value: e.target.value } : v))}
                        />
                      )}
                      {maxLength && (
                        <p className="text-xs text-foreground-400 text-right">
                          {variant.value.length}/{maxLength}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-background-50 border-t border-foreground-100 flex justify-between rounded-b-2xl">
              <div className="pmt-4">
                <LanguageSelectDropdown
                  availableLanguages={remainingDraftLanguages}
                  onSelect={handleLanguageSelect}
                />
              </div>
              <button
                onClick={() => {
                  updateVariants(draftVariants.filter(v => v.value.trim() !== ''));
                  setIsModalOpen(false);
                }}
                className="px-6 py-2.5 bg-secondary-950 hover:bg-secondary-900 text-white text-sm font-medium rounded-xl transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
